import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { Box, Button, Card, CardContent, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { Home, LocationOn, MyLocation } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { GoogleMap, Marker } from '@react-google-maps/api';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGoogleMapsApiLoader } from '@frontend/features/common/hooks/useGoogleMapsApi';
import { QueryKeys } from '@frontend/features/common/types';
import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';

import { latLongValidation } from '../../helpers';
import { useGetHotelData, useUpdateHotelLocationMutation } from '../../queries';
import { useHotelMetadata } from '../../hooks';
import { SupplierInfoText } from './styles';
import { useLoader } from './Loader';

const ID_KEY = 'location-editor';

interface Coords {
  lat: number;
  lng: number;
}

const zoom = 14;

const style = {
  width: '100%',
  height: '400px',
};

const useValidationSchema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    latLong: yup
      .string()
      .required(t('hotels.contentManager.detail.location.required'))
      .test('is-valid-lat-long', t('hotels.contentManager.detail.location.invalid'), latLongValidation),
    address: yup
      .string()
      .required(t('hotels.contentManager.detail.address.required'))
  });
};

// eslint-disable-next-line react/display-name
export const LocationEditor = React.memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const { isMediumUp } = useBreakpoints();

  const queryClient = useQueryClient();
  const { hotelId } = useParams();
  const { isLoading: loadingMeta, findSupplier} = useHotelMetadata();
  const {
    isLoading: loadingData,
    data: hotel
  } = useGetHotelData({
    hotelId: `${hotelId}`,
  });
  const { toggleLoader } = useLoader();

  const [hotelMarker, setHotelMarker] = useState<Coords | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded: mapLoaded } = useGoogleMapsApiLoader();

  const center = useMemo(() => ({ lat: hotel?.latitude || 0, lng: hotel?.longitude || 0 }), [hotel]);

  const validationSchema = useValidationSchema();
  
  const {
    control,
    formState: { errors },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { mutate: updateLocation } = useUpdateHotelLocationMutation({
    onMutate: () => {
      toggleLoader(ID_KEY, true);
    },
    onSettled: () => {
      toggleLoader(ID_KEY, false);
    },
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('hotels.contentManager.detail.location.update.success'),
      });
      queryClient.invalidateQueries([QueryKeys.getHotelData, hotelId]);
      queryClient.invalidateQueries([QueryKeys.getHotelCardData, hotelId]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('hotels.contentManager.detail.location.update.error'),
      });
    },
  });

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
    },
    [hotel],
  );

  const handleUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleUpdateLocationValue = (lat?: number, lng?: number, address?: string) => {
    if (lat && lng) {
      setHotelMarker({ lat, lng });
      setValue('latLong', `${lat}, ${lng}`);
    }
    if (address) {
      setValue('address', address);
    }
  };

  const addMarker = ({ latLng }: google.maps.MapMouseEvent) => {
    if (latLng) {
      const { lat, lng } = latLng;
      handleUpdateLocationValue(lat(), lng());
    }
  };

  useEffect(() => {
    if (!mapLoaded || loadingData || loadingMeta) {
      toggleLoader(ID_KEY, true);
    } else {
      toggleLoader(ID_KEY, false);
      if (hotel) {
        setHotelMarker({
          lat: hotel.latitude,
          lng: hotel.longitude,
        });
        reset({
          latLong: `${hotel.latitude}, ${hotel.longitude}`,
          address: hotel.address,
        });
      }
    }
  }, [hotel, mapLoaded, loadingData, loadingMeta]);


  const saveLocation = (data: { latLong: string, address: string }) => {
    if (hotel?.id) {
      const { address } = data;
      const [latitude, longitude] = data.latLong.trim().split(',');
      updateLocation({
        hotelId: hotel.id,
        address,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
      });
    }
  };

  // TODO: Get tgx locations for hotel
  const tgxLocationsForHotel: {
    id: number;
    supplierId: string;
    name: string;
    latitude: number;
    longitude: number;
    address: string;
  }[] = [];

  return (
    <Box padding={theme.spacing(2)}>
      <Card elevation={2} sx={{ mt: theme.spacing(2) }}>
        <CardContent>
          {hotel && (
            <>
              <form onSubmit={handleSubmit(saveLocation)}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">{t('hotels.contentManager.detail.location.title')}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size='small'
                  >
                    {t('common.form.save')}
                  </Button>
                </Box>
                <Box
                  display="flex"
                  gap={theme.spacing(2)}
                  my={theme.spacing(3)}
                  flexWrap={isMediumUp ? 'nowrap' : 'wrap'}
                >
                  <Controller
                    name={'latLong'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn />
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.latLong}
                        helperText={errors.latLong ? errors.latLong.message : ''}
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    )}
                  />
                  <Controller
                    name={'address'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Home />
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.address}
                        helperText={errors.address ? errors.address.message : ''}
                      />
                    )}
                  />
                </Box>
              </form>
              <Box sx={style}>
                {mapLoaded && (
                  <GoogleMap
                    mapContainerStyle={style}
                    center={center}
                    zoom={zoom}
                    onClick={addMarker}
                    onLoad={handleLoad}
                    onUnmount={handleUnmount}
                    options={{
                      mapTypeControl: false,
                      streetViewControl: false,
                      zoomControl: true,
                    }}
                  >
                    {hotelMarker && (
                      <Marker
                        position={{ lat: hotelMarker.lat, lng: hotelMarker.lng }}
                      />
                    )}
                  </GoogleMap>
                )}
              </Box>

              {/* TODO: Reutilizar, cuando pidamos directo a tgx */}
              {false && tgxLocationsForHotel?.map(
                (location) => {
                  return (
                    <Box key={location.id}>
                      <Typography variant="h6" align="left" my={theme.spacing(3)}>
                        {findSupplier(parseInt(location.supplierId))?.supplierName}
                      </Typography>
  
                      <Typography variant="subtitle2" align="left" mb={theme.spacing(3)}>
                        {location.name}
                      </Typography>
  
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={theme.spacing(2)}
                      >
                        <SupplierInfoText
                          fullWidth
                          size="small"
                          disabled
                          value={`${location.latitude}, ${location.longitude}`}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MyLocation />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <SupplierInfoText
                          fullWidth
                          size="small"
                          disabled
                          value={location.address}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Home />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            minWidth: 150
                          }}
                          onClick={() => handleUpdateLocationValue(location.latitude, location.longitude, location.address)}
                        >
                          {t('hotels.contentManager.detail.location.showInMap')}
                        </Button>
                      </Box>
                    </Box>
                  );
                }
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});
